import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { MarketplaceStrategy } from "@/types/strategy-types";
import { AccountPerformanceData } from "./types/strategy-card-types";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionStatus } from "@/hooks/account/useSubscriptionStatus";
import StrategyCardHeader from "./strategy-card/StrategyCardHeader";
import StrategyCardDetails from "./strategy-card/StrategyCardDetails";
import StrategyCardAction from "./strategy-card/StrategyCardAction";
import BlockedInCountryOverlay from "./strategy-card/BlockedInCountryOverlay";
import { Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface StrategyCardProps {
  strategy: MarketplaceStrategy;
  onSelect: (strategyName: string) => void;
  getRiskLabel?: (level: number) => { text: string; color: string };
  formatDate?: (dateString: string) => string;
  getExchangeLogo?: (exchange: string) => string;
  performanceData?: AccountPerformanceData;
  unfollowAction?: () => void;
  showUnfollowButton?: boolean;
  followLabel?: string;
  isBlockedInCountry?: boolean;
}

const StrategyCard = ({
  strategy,
  onSelect,
  getRiskLabel,
  formatDate,
  getExchangeLogo = (exchange: string) => `/lovable-uploads/6a93bfa2-7afa-404d-8a62-9c96df3c95ee.png`,
  performanceData,
  unfollowAction,
  showUnfollowButton = false,
  followLabel = "Start Following Now",
  isBlockedInCountry = false
}: StrategyCardProps) => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  
  const isPro = strategy.plan === 'premium' || strategy.plan === 'pro' || strategy.is_pro;
  const isComingSoon = strategy.approved === true && strategy.active === false;
  
  const { subscriptionStatus, loading: subscriptionLoading } = useSubscriptionStatus(userId);
  const hasActiveSubscription = !subscriptionLoading && subscriptionStatus.status === "active";

  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setUserId(user.id);
        }
      } catch (err) {
        console.error("Error getting user:", err);
      }
    };
    
    getUser();
  }, []);

  const handleStrategyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isComingSoon || isBlockedInCountry) return;
    
    onSelect(strategy.strategy_name || strategy.name || '');
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isComingSoon || isBlockedInCountry) return;
    
    const strategyName = strategy.strategy_name || strategy.name || '';
    
    const riskLabelInfo = getRiskLabel 
      ? getRiskLabel(strategy.risk_level)
      : { text: strategy.risk_level === 1 ? "Low" : strategy.risk_level === 2 ? "Medium" : "High" };
    
    const formattedPerformance = performanceData?.pnl_ratio 
      ? `${performanceData.pnl_ratio.toFixed(2)}%` 
      : `${strategy.performance_percentage || 0}%`;
    
    const strategyData = {
      id: strategy.id,
      name: strategyName,
      performance: formattedPerformance,
      riskLevel: riskLabelInfo.text,
      minInvestment: `${strategy.min_investment.toLocaleString()} ${strategy.currency || 'USDT'}`,
      since: formatDate ? formatDate(strategy.performance_since || '') : (strategy.performance_since || ''),
      exchange: strategy.exchange,
      description: strategy.description,
      followers: strategy.total_followers ?? strategy.followers_count ?? 0,
      aum: strategy.total_aum ?? performanceData?.aum ?? null,
      winRate: performanceData?.win_rate ?? strategy.win_rate,
      maxDrawdown: performanceData?.max_drawdown ?? strategy.max_drawdown,
      tradesWon: performanceData?.num_wins ?? strategy.trades_won,
      tradesLost: performanceData?.num_losses ?? strategy.trades_lost,
      isPremium: isPro,
      currency: strategy.currency || 'USDT'
    };

    if (isPro && hasActiveSubscription) {
      navigate('/follow-strategy-premium-existing', {
        state: {
          strategy: strategyData,
          originalStrategyId: strategy.id
        }
      });
    } else if (isPro) {
      navigate('/follow-strategy', {
        state: {
          strategy: strategyData,
          originalStrategyId: strategy.id
        }
      });
    } else {
      navigate('/follow-strategy-free', {
        state: {
          strategy: strategyData,
          originalStrategyId: strategy.id
        }
      });
    }
  };

  const handleUnfollow = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (unfollowAction) {
      unfollowAction();
    }
  };

  const strategyData = {
    id: strategy.id,
    name: strategy.strategy_name || strategy.name || '',
    exchange: strategy.exchange,
    description: strategy.description,
    isPremium: isPro,
    currency: strategy.currency || 'USDT',
    minInvestment: strategy.min_investment,
    risk_level: strategy.risk_level,
    performance_percentage: strategy.performance_percentage,
    performance_since: strategy.performance_since
  };

  const cardClass = isPhemexLanding ? "phemex-landing-card" : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      onClick={handleStrategyClick}
      className={`cursor-pointer w-full max-w-sm ${cardClass} ${(isComingSoon || isBlockedInCountry) ? 'pointer-events-none relative' : ''}`}
    >
      <Card 
        className={`relative overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 rounded-xl h-full flex flex-col ${
          isPro 
            ? "border-[#C3DAEC]" 
            : "border-gray-200"
        } ${(isComingSoon || isBlockedInCountry) ? 'opacity-90' : ''}`}
      >
        {showUnfollowButton && !isComingSoon && !isBlockedInCountry && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 z-10 text-white hover:text-red-500 hover:bg-red-50 rounded-full"
            onClick={handleUnfollow}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
        
        {isComingSoon ? (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-20 flex flex-col items-center justify-center p-6 rounded-xl">
            <Clock className="w-12 h-12 text-white/90 mb-2" />
            <h3 className="text-xl font-bold text-white text-center mb-1">Coming Soon</h3>
            <p className="text-white/80 text-center text-sm mb-3">
              This strategy will be available soon
            </p>
            
            <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg mb-2">
              <span className="text-white font-medium text-sm">
                {strategy.min_investment.toLocaleString()} {strategy.currency || 'USDT'}
              </span>
              <span className="text-white/70 text-xs">Min. Investment</span>
            </div>
            
            <div className="bg-white/10 p-2 rounded-lg flex items-center justify-center">
              <img 
                src={getExchangeLogo(strategy.exchange)}
                alt={`${strategy.exchange} logo`}
                className="h-5 w-auto object-contain"
              />
            </div>
          </div>
        ) : isBlockedInCountry ? (
          <BlockedInCountryOverlay exchange={strategy.exchange} />
        ) : null}
        
        <StrategyCardHeader
          name={strategy.strategy_name || strategy.name || ''}
          minInvestment={strategy.min_investment}
          isPro={isPro}
          currency={strategy.currency || 'USDT'}
        />
        
        <StrategyCardDetails
          isPro={isPro}
          followerCount={strategy.total_followers ?? strategy.followers_count ?? 0}
          pnlRatio={performanceData?.pnl_ratio !== null ? performanceData?.pnl_ratio : strategy.performance_percentage || 0}
          timespan={performanceData?.timespan}
          exchange={strategy.exchange}
          getExchangeLogo={getExchangeLogo}
          winRate={performanceData?.win_rate ?? strategy.win_rate}
          maxDrawdown={performanceData?.max_drawdown ?? strategy.max_drawdown}
          tradesWon={performanceData?.num_wins ?? strategy.trades_won}
          tradesLost={performanceData?.num_losses ?? strategy.trades_lost}
          aum={strategy.total_aum ?? performanceData?.aum ?? null}
          description={strategy.description}
          performanceSince={strategy.performance_since}
          formatDate={formatDate}
          isComingSoon={isComingSoon}
        />
        
        <StrategyCardAction
          isPro={isPro}
          hasActiveSubscription={hasActiveSubscription}
          onButtonClick={handleButtonClick}
          label={followLabel}
          isComingSoon={isComingSoon}
          strategyData={strategyData}
        />
      </Card>
    </motion.div>
  );
};

export default StrategyCard;
